import slugify from 'slugify'
import { load } from 'cheerio'

import { TableOfContentsProps } from '../../components/molecules/TableOfContents'
import { ArticleBlockUnion } from '../../graphql/generated/api-graphql'
import { escapeHtmlForJsonLd } from '../../helpers/StringHelpers'
import { ArticleRecipesBlockProps } from '../../components/molecules/ArticleRecipesBlock'

const getHeadingWithPosition = (
  str: string,
  heading: number
): { text: string; position: number; heading: number }[] => {
  const $ = load(str)
  const matches: {
    text: string
    position: number
    heading: number
  }[] = []

  $(`h${heading}`).each((_, element) => {
    matches.push({
      text: escapeHtmlForJsonLd($(element).text()),
      position: $(element).index(),
      heading: heading,
    })
  })

  return matches
}

const buildIntro = (
  introduction: string,
  blocks: ArticleBlockUnion[]
): TableOfContentsProps['contents'] => {
  const lvl2 = getHeadingWithPosition(introduction, 2)
  const lvl3 = getHeadingWithPosition(introduction, 3)

  return lvl2.map((heading2, index) => {
    return {
      title: heading2.text,
      anchor: slugify(heading2.text),
      level: 2,
      child: lvl3
        .filter((heading3) => heading3.position > heading2.position)
        .map((heading3) => ({
          title: heading3.text,
          anchor: slugify(heading3.text),
          level: 3,
        }))
        .concat(
          index === lvl2.length - 1
            ? blocks
                .map((block) => {
                  if (block.__typename === 'ArticleEditorBlock') {
                    const title = block.title || ''
                    return {
                      title,
                      anchor: slugify(title),
                      level: 3,
                    }
                  }
                  return []
                })
                .flat()
            : []
        ),
    }
  })
}

const isBlockAlreadyInTableOfContents = (
  block: ArticleBlockUnion,
  intro: TableOfContentsProps['contents']
) => {
  return block.__typename === 'ArticleEditorBlock' && intro && intro.length > 0
}

export const buildTableOfContents = (
  introduction: string,
  blocks: ArticleBlockUnion[],
  articleRecipes: ArticleRecipesBlockProps | null,
  t: any
) => {
  const intro = buildIntro(introduction, blocks)

  return {
    title: t('article_table_of_contents_title'),
    contents: [
      ...intro,
      ...((articleRecipes &&
        articleRecipes?.recipes?.length > 0 &&
        articleRecipes?.recipes.map((recipe) => ({
          title: recipe.title,
          anchor: slugify(recipe?.title || ''),
          level: 2,
        }))) ||
        []),
      ...blocks.flatMap((block) => {
        if (isBlockAlreadyInTableOfContents(block, intro)) {
          return []
        }

        const title = 'title' in block ? block.title : null
        const lvl2 = getHeadingWithPosition(block?.description || '', 2).sort(
          (a, b) => a.position - b.position
        )
        const lvl3 = getHeadingWithPosition(block?.description || '', 3).sort(
          (a, b) => a.position - b.position
        )

        const blockEntries = []

        if (title) {
          const nextH2Position = lvl2[0]?.position || Infinity
          blockEntries.push({
            title,
            anchor: slugify(title),
            level: 2,
            child: lvl3
              .filter((heading3) => heading3.position < nextH2Position)
              .map((lvl3) => ({
                title: lvl3.text,
                anchor: slugify(lvl3.text),
                level: 3,
              })),
          })
        }

        blockEntries.push(
          ...lvl2.map((heading2, index) => {
            const nextH2Position = lvl2[index + 1]?.position || Infinity
            return {
              title: heading2.text,
              anchor: slugify(heading2.text),
              level: 2,
              child: lvl3
                .filter(
                  (heading3) =>
                    heading3.position > heading2.position &&
                    heading3.position < nextH2Position
                )
                .map((heading3) => ({
                  title: heading3.text,
                  anchor: slugify(heading3.text),
                  level: 3,
                })),
            }
          })
        )

        return blockEntries
      }),
    ] as TableOfContentsProps['contents'],
  }
}

export const insertIdsInIntro = (introduction: string): string => {
  const $ = load(introduction)
  $('h2').each((_, elem) => {
    const id = slugify($(elem).text())
    $(elem).attr('id', id)
  })
  $('h3').each((_, elem) => {
    const id = slugify($(elem).text())
    $(elem).attr('id', id)
  })
  return $.html()
}

export const insertIdsInBlocks = (
  blocks: ArticleBlockUnion[]
): ArticleBlockUnion[] => {
  const blocksWithIds = blocks.map((block) => {
    const $ = load(block.description || '')

    if ('title' in block) {
      // @ts-ignore
      block.anchor = slugify(block?.title || '')
    } else {
      $('h2').each((_, elem) => {
        const id = slugify($(elem).text())
        $(elem).attr('id', id)
      })
      block.description = $.html()
    }

    $('h3').each((_, elem) => {
      const id = slugify($(elem).text())
      $(elem).attr('id', id)
    })
    block.description = $.html()

    return block
  })

  return blocksWithIds
}
